import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, CheckCircle2, XCircle, RefreshCw,
  Trash2, Play, AlertCircle, BookmarkCheck, RotateCcw, Lightbulb, FileText, ClipboardList
} from 'lucide-react';
import { RoadSign } from '../components/RoadSign';
import { SpeakButton } from '../components/SpeakButton';
import { LexiqueDrawer } from '../components/LexiqueDrawer';

const cleanChoice = (c) => String(c).replace(/^[A-D][.:)]\s*/i, '').trim();
const letterToIndex = (l) => ({ A: 0, B: 1, C: 2, D: 3 }[String(l).toUpperCase()] ?? 0);

const THEME_LABELS = {
  R: 'La route', C: 'La circulation', L: 'Le conducteur', U: 'Les autres usagers',
  D: 'Notions diverses', M: 'Mécanique', P: 'Équipements', E: 'Environnement', S: 'Premiers secours'
};
const THEME_COLORS = {
  R: '#cf2031', C: '#1d4ed8', L: '#7c3aed', U: '#db2777', D: '#0891b2',
  M: '#f59e0b', P: '#18794e', E: '#16a34a', S: '#b91c1c'
};

export default function ARevoir() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [data, setData] = useState({ total: 0, by_theme: {}, questions: [] });
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('list'); // list | training
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [sessionStats, setSessionStats] = useState({ good: 0, total: 0 });

  useEffect(() => {
    if (!user) return;
    loadErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadErrors = async () => {
    setLoading(true);
    try {
      const d = await api.getErrors();
      setData(d);
    } catch (e) {
      toast.error('Impossible de charger les erreurs');
    } finally {
      setLoading(false);
    }
  };

  const startTraining = (themeCode = null) => {
    const pool = themeCode
      ? data.questions.filter(q => q.theme_code === themeCode)
      : data.questions;
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setQueue(shuffled);
    setSelectedTheme(themeCode);
    setIdx(0);
    setSelected(null);
    setLocked(false);
    setSessionStats({ good: 0, total: 0 });
    setStage('training');
  };

  const handleAnswer = async (choiceIdx) => {
    if (locked) return;
    const current = queue[idx];
    const correctIdx = letterToIndex(current.correct_answer);
    const isCorrect = choiceIdx === correctIdx;
    setSelected(choiceIdx);
    setLocked(true);
    setSessionStats(s => ({ good: s.good + (isCorrect ? 1 : 0), total: s.total + 1 }));
    try {
      await api.recordQCMDetailed(isCorrect, current.id, current.theme_code);
    } catch (e) { /* silent */ }
  };

  const handleNext = () => {
    if (idx + 1 >= queue.length) {
      // Fin de session
      setStage('list');
      loadErrors();
      toast.success(`Séance terminée : ${sessionStats.good + (selected !== null && queue[idx] && selected === letterToIndex(queue[idx].correct_answer) ? 1 : 0)}/${sessionStats.total + 1} réussies`);
    } else {
      setIdx(idx + 1);
      setSelected(null);
      setLocked(false);
    }
  };

  const handleRemove = async (qid) => {
    try {
      await api.removeError(qid);
      toast.success('Question retirée de la liste');
      loadErrors();
    } catch (e) { toast.error('Échec'); }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Vider la liste « À revoir » ?')) return;
    try {
      await api.clearErrors();
      toast.success('Liste vidée');
      loadErrors();
    } catch (e) { toast.error('Échec'); }
  };

  // --- Non connecté ---
  if (!user) {
    return (
      <div data-testid="revoir-page">
      <div className="mc-card p-8 text-center" data-testid="revoir-login">
        <AlertCircle className="w-10 h-10 text-[hsl(var(--primary))] mx-auto mb-3" />
        <h2 className="text-xl font-black mb-2">Connexion requise</h2>
        <p className="text-sm text-muted-foreground mb-4">Connecte-toi pour retrouver tes erreurs et les retravailler.</p>
        <div className="flex gap-2 justify-center flex-wrap">
          <Button className="mc-btn-primary" onClick={() => navigate('/login')} data-testid="revoir-login-btn">Se connecter</Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/register')}>Créer un compte</Button>
        </div>
      </div>
      </div>
    );
  }

  // --- Training stage ---
  if (stage === 'training' && queue[idx]) {
    const current = queue[idx];
    const correctIdx = letterToIndex(current.correct_answer);
    const themeColor = THEME_COLORS[current.theme_code] || '#cf2031';
    return (
      <div className="space-y-4" data-testid="revoir-page">
        <div data-testid="revoir-training">
        <div className="mc-card p-3 sm:p-4 flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setStage('list')} className="gap-1.5" data-testid="revoir-quit">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              À revoir · Thm {current.theme_code}
            </div>
            <div className="text-sm font-bold">{THEME_LABELS[current.theme_code] || '—'}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Question</div>
            <div className="text-lg font-black">{idx + 1} / {queue.length}</div>
          </div>
        </div>

        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${((idx + (locked ? 1 : 0)) / queue.length) * 100}%`, background: themeColor }} />
        </div>

        <div className="mc-card p-5 sm:p-6" data-testid="revoir-question">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider" style={{ background: `${themeColor}15`, color: themeColor }}>
              {current.difficulty || 'standard'} · ratée {current.error_count}×
            </span>
          </div>
          {current.sign_code && (
            <div className="flex items-center justify-center mb-5 p-3 rounded-2xl bg-[hsl(var(--panel))]/60 border border-border" data-testid="revoir-sign">
              <RoadSign code={current.sign_code} size={96} />
            </div>
          )}
          <h3 className="text-lg sm:text-xl font-black leading-tight mb-5">{current.question}</h3>
          <div className="flex items-center gap-2 mb-4 -mt-2 hide-in-oral no-print">
            <SpeakButton
              text={current.question}
              choices={current.choices?.map(cleanChoice) || []}
              testId="revoir-speak"
              label="Écouter"
              compact
            />
            <span className="text-[11px] text-muted-foreground">
              Lecture audio de la question
            </span>
          </div>
          <div className="grid gap-2.5">
            {current.choices.map((c, i) => {
              const isSelected = selected === i;
              const isRight = i === correctIdx;
              let cls = 'bg-card border-border hover:border-foreground/30 active:scale-[0.99]';
              if (locked) {
                if (isRight) cls = 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] border-[#c8edd6]';
                else if (isSelected) cls = 'bg-[#fff1f2] text-[#b42318] border-[#fecdd3]';
                else cls = 'bg-card border-border opacity-50';
              }
              return (
                <button
                  key={i}
                  onClick={() => handleAnswer(i)}
                  disabled={locked}
                  data-testid={`revoir-answer-${i}`}
                  className={cn('w-full text-left rounded-2xl border-2 p-4 text-sm sm:text-base font-semibold transition-all flex items-center gap-3 min-h-[56px]', cls)}
                >
                  <span className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-black" style={{
                    background: locked && isRight ? 'hsl(var(--success))' :
                                locked && isSelected && !isRight ? '#b42318' : 'hsl(var(--panel))',
                    color: locked && (isRight || (isSelected && !isRight)) ? 'white' : 'inherit'
                  }}>
                    {locked && isRight ? <CheckCircle2 className="w-4 h-4" /> :
                     locked && isSelected && !isRight ? <XCircle className="w-4 h-4" /> :
                     String.fromCharCode(65 + i)}
                  </span>
                  <span className="flex-1">{cleanChoice(c)}</span>
                </button>
              );
            })}
          </div>

          {locked && (
            <div className="mt-5">
              {current.explanation && (
                <div className="p-3 rounded-lg bg-muted/50 text-sm" data-testid="revoir-explanation">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5"><Lightbulb className="w-3 h-3" /><span>Explication</span></div>
                  {current.explanation}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-bold" style={{ color: selected === correctIdx ? '#18794e' : '#b42318' }}>
                  {selected === correctIdx ? '✓ Bonne réponse — retirée de « À revoir »' : `✗ Bonne réponse : ${String.fromCharCode(65 + correctIdx)}`}
                </div>
                <Button onClick={handleNext} className="mc-btn-primary" data-testid="revoir-next">
                  {idx + 1 >= queue.length ? 'Terminer la séance' : 'Question suivante'}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    );
  }

  // --- Liste des erreurs ---
  return (
    <div className="space-y-5" data-testid="revoir-page">
      <div data-testid="revoir-list">
      {/* Hero */}
      <section className="mc-card relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full blur-3xl opacity-30" style={{ background: '#cf2031' }} />
        <div className="relative p-6 sm:p-8">
          <Button onClick={() => navigate('/revision')} variant="outline" size="sm" className="rounded-full bg-white/10 border-white/20 text-foreground hover:bg-white/20 hover:text-foreground mb-4" data-testid="revoir-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Révision
          </Button>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[10px] font-black text-foreground uppercase tracking-widest mb-3">
            📌 À revoir
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground leading-tight">
            Tes erreurs, transformées en<br />
            <span className="text-[#ff7a84]">progression.</span>
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-xl">
            Chaque question ratée est mémorisée. Retravaille-les jusqu'à réussir : elles disparaissent automatiquement dès que tu donnes la bonne réponse.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="mc-card p-10 text-center">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[hsl(var(--primary))] mb-3" />
          <p className="text-sm text-muted-foreground">Chargement de tes erreurs…</p>
        </div>
      ) : data.total === 0 ? (
        <div className="mc-card p-10 text-center" data-testid="revoir-empty">
          <BookmarkCheck className="w-12 h-12 text-[#18794e] mx-auto mb-3" />
          <h2 className="text-2xl font-black mb-2">Aucune erreur à revoir !</h2>
          <p className="text-sm text-muted-foreground mb-5">Excellente maîtrise. Continue avec un nouveau thème ou découvre une enquête.</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button onClick={() => navigate('/revision')} variant="outline" className="rounded-full">Apprendre par thème</Button>
            <Button onClick={() => navigate('/enquetes')} className="mc-btn-primary">
              <Play className="w-4 h-4 mr-1.5" /> Découvrir les enquêtes
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Stats + actions */}
          <section className="mc-card p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="revoir-stats">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Questions à retravailler</div>
              <div className="text-4xl font-black text-[hsl(var(--primary))]">{data.total}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => startTraining(null)} className="mc-btn-primary h-11" data-testid="revoir-start-all">
                <Play className="w-4 h-4 mr-1.5" /> Retravailler tout
              </Button>
              <Button onClick={handleClearAll} variant="outline" className="rounded-full h-11 text-[#b42318] border-[#fecdd3] hover:bg-[#fff1f2]" data-testid="revoir-clear-all">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Tout effacer
              </Button>
            </div>
          </section>

          {/* Par thème */}
          <section className="mc-card p-5" data-testid="revoir-by-theme">
            <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" /><span>Par thème</span></h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(data.by_theme).sort((a, b) => b[1] - a[1]).map(([code, count]) => (
                <button
                  key={code}
                  onClick={() => startTraining(code)}
                  data-testid={`revoir-theme-${code}`}
                  className="mc-card p-4 text-left hover:-translate-y-0.5 hover:shadow-lg transition-all flex items-center gap-3"
                  style={{ borderLeft: `4px solid ${THEME_COLORS[code] || '#cf2031'}` }}
                >
                  <div className="text-2xl font-black w-10 text-center" style={{ color: THEME_COLORS[code] || '#cf2031' }}>{code}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{THEME_LABELS[code] || code}</div>
                    <div className="text-xs text-muted-foreground">{count} erreur(s)</div>
                  </div>
                  <Play className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </section>

          {/* Liste détaillée (10 premières) */}
          <section className="mc-card p-5" data-testid="revoir-questions-list">
            <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /><span>Tes {Math.min(data.questions.length, 10)} dernières erreurs</span></h2>
            <div className="space-y-2">
              {data.questions.slice(0, 10).map((q) => (
                <div key={q.id} className="p-3 rounded-lg border border-border flex items-start gap-3" data-testid={`revoir-item-${q.id}`}>
                  <span className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm" style={{
                    background: `${THEME_COLORS[q.theme_code] || '#cf2031'}15`,
                    color: THEME_COLORS[q.theme_code] || '#cf2031'
                  }}>{q.theme_code}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{q.question}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Ratée {q.error_count}× · Bonne réponse : {q.correct_answer}</div>
                  </div>
                  <Button onClick={() => handleRemove(q.id)} variant="outline" size="sm" className="flex-shrink-0" data-testid={`revoir-remove-${q.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-[#b42318]" />
                  </Button>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      </div>
      {/* Lexique drawer — accessible depuis l'écran "À revoir" (touche L) */}
      <LexiqueDrawer />
    </div>
  );
}
